import React, { useState } from 'react';
import './FlagModal.css';

/**
 * Reusable Flag Modal Component
 * Used for reporting inappropriate content across the site
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onSubmit - Callback when flag is submitted (reason, message)
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 * @param {boolean} props.alreadyFlagged - Whether user already flagged this content
 * @param {Array} props.flagReasons - Array of {value, label} for dropdown options
 * @param {string} props.contentType - Type of content being flagged (for display)
 */
const FlagModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  alreadyFlagged = false,
  flagReasons = [],
  contentType = 'content',
}) => {
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(reason, message);
  };

  const handleClose = () => {
    setReason('');
    setMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="flag-modal-overlay" onClick={handleClose}>
      <div className="flag-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flag-modal-header">
          <h3><i className="bi bi-flag"></i> Flag Content</h3>
          <button className="flag-modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="flag-modal-body">
          <div className="flag-modal-content">
            <p>Help us keep our community safe by reporting inappropriate content.</p>

            {alreadyFlagged ? (
              <div className="flag-already-submitted">
                <i className="bi bi-exclamation-circle"></i> You have already flagged this {contentType}.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="flag-form-group">
                  <label htmlFor="flagReason">Reason for flagging</label>
                  <select
                    id="flagReason"
                    className="flag-select"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={isSubmitting}
                  >
                    {flagReasons.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flag-form-group">
                  <label htmlFor="flagMessage">Additional details</label>
                  <textarea
                    id="flagMessage"
                    className="flag-textarea"
                    rows="4"
                    placeholder="Please provide more details about why you're flagging this content..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={isSubmitting}
                    maxLength={1000}
                  />
                  <div className="flag-textarea-footer">
                    <span className={`char-counter ${message.length < 10 ? 'insufficient' : 'sufficient'}`}>
                      {message.length < 10 ? (
                        <><i className="bi bi-info-circle"></i> {10 - message.length} more characters needed</>
                      ) : (
                        <><i className="bi bi-check-circle"></i> {message.length}/1000</>
                      )}
                    </span>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="flag-modal-footer">
          <button
            type="button"
            className="flag-btn-cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          {!alreadyFlagged && (
            <button
              type="submit"
              className="flag-btn-submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="flag-spinner"></span> Submitting...
                </>
              ) : (
                <>
                  <i className="bi bi-flag"></i> Submit Flag
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlagModal;
